/**
 * Modeville product-card colour slideshow.
 *
 * Each card holds ONE representative photo per colour (see snippets/mv-product-card.liquid).
 * Mouse: the first different colour appears within 1 second of the pointer arriving (about 0.8 s), then another
 *   different colour every 1 second, looping. When the pointer leaves, the cycle stops and the card returns to its
 *   first photo. Photos stay inside the image area, so the card never resizes or shifts.
 *   Only photos that have finished loading are shown, and the card's photos are loaded in advance (when the card
 *   scrolls into view and again when the pointer arrives), so a change is never a blank frame.
 * Touch (phones / tablets): no autoplay. Swipe the photo left or right, or tap the dots, to move between colours.
 *   A swipe never opens the product; a plain tap on the photo still does.
 * Reduced motion: no automatic changes at all (manual swipe / dots still work).
 * Cards with fewer than 2 colour photos stay static.
 * Nothing here touches the purchasing variant: the swatches, title and link are left alone.
 *
 * Works on Modeville cards (.mv-card) and, for any page that still uses them, Horizon <product-card> elements.
 */
(() => {
  if (window.__mvCardSlideshow) return;
  window.__mvCardSlideshow = true;

  const FIRST_MS = 800; // first change, comfortably inside one second of the pointer arriving
  const INTERVAL_MS = 1000; // a different colour every second after that
  const SWIPE_PX = 36;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {WeakMap<Element, {first?: number, loop?: number}>} */
  const timers = new WeakMap();
  /** @type {WeakMap<Element, number>} */
  const current = new WeakMap();
  /** @type {WeakMap<Element, number>} */
  const lastSwipe = new WeakMap();

  const isHomeCard = (card) => card.classList.contains('mv-card');
  const slideshowOf = (card) => card.querySelector('.card-gallery slideshow-component');
  const homeImages = (card) => Array.from(card.querySelectorAll('.mv-card__img'));
  const homeDots = (card) => Array.from(card.querySelectorAll('.mv-dot'));
  const isReady = (img) => img.complete && img.naturalWidth > 0;

  const showHomeImage = (card, index) => {
    current.set(card, index);
    homeImages(card).forEach((img, i) => img.classList.toggle('is-active', i === index));
    homeDots(card).forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  };

  const preload = (card) => {
    homeImages(card).forEach((img) => {
      if (img.loading === 'lazy') img.loading = 'eager';
    });
  };

  // Load a card's other colours once the card is near the screen, so they are ready before anyone hovers.
  if ('IntersectionObserver' in window) {
    const seen = new WeakSet();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          preload(entry.target);
        });
      },
      { rootMargin: '300px 0px' }
    );
    const watch = (root) => {
      root.querySelectorAll('.mv-card').forEach((card) => {
        if (seen.has(card) || homeImages(card).length < 2) return;
        seen.add(card);
        io.observe(card);
      });
    };
    const begin = () => {
      watch(document);
      // cards added later (filters, extra pages, search) are picked up here
      new MutationObserver((records) => {
        records.forEach((record) =>
          record.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            if (node.matches('.mv-card') && !seen.has(node) && homeImages(node).length > 1) {
              seen.add(node);
              io.observe(node);
            } else {
              watch(node);
            }
          })
        );
      }).observe(document.body, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', begin);
    else begin();
  }

  const cardFrom = (event) => {
    const pointerType = event.pointerType;
    if (pointerType && pointerType !== 'mouse') return null;
    const target = event.target;
    return target instanceof Element && target.matches('product-card, .mv-card') ? target : null;
  };

  const start = (card) => {
    if (reducedMotion.matches || timers.has(card)) return;

    if (isHomeCard(card)) {
      if (homeImages(card).length < 2) return;
      preload(card);
      const step = () => {
        const images = homeImages(card);
        const from = current.get(card) || 0;
        for (let k = 1; k < images.length; k += 1) {
          const next = (from + k) % images.length;
          if (isReady(images[next])) {
            showHomeImage(card, next);
            return;
          }
        }
      };
      const entry = {};
      entry.first = window.setTimeout(() => {
        step();
        entry.loop = window.setInterval(step, INTERVAL_MS);
      }, FIRST_MS);
      timers.set(card, entry);
      return;
    }

    const slideshow = slideshowOf(card);
    if (!slideshow || slideshow.getAttribute('disabled') === 'true') return;
    if ((slideshow.slides || []).length < 2) return;
    card.querySelectorAll('.card-gallery img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
    const advance = () => {
      const slides = slideshow.slides || [];
      if (slides.length < 2) return;
      slideshow.select((slideshow.current + 1) % slides.length, undefined, { animate: false });
    };
    const entry = {};
    entry.first = window.setTimeout(() => {
      advance();
      entry.loop = window.setInterval(advance, INTERVAL_MS);
    }, FIRST_MS);
    timers.set(card, entry);
  };

  const stop = (card) => {
    const entry = timers.get(card);
    if (entry) {
      if (entry.first !== undefined) window.clearTimeout(entry.first);
      if (entry.loop !== undefined) window.clearInterval(entry.loop);
      timers.delete(card);
    }
    if (isHomeCard(card)) {
      if ((current.get(card) || 0) !== 0) showHomeImage(card, 0);
      return;
    }
    const slideshow = slideshowOf(card);
    if (slideshow && slideshow.current > 0) slideshow.select(0, undefined, { animate: false });
  };

  // pointerenter / pointerleave do not bubble but can be caught while capturing, so one pair of document listeners
  // covers every card, including ones added later (filters, pagination, quick view).
  document.addEventListener(
    'pointerenter',
    (event) => {
      const card = cardFrom(event);
      if (card) start(card);
    },
    true
  );
  document.addEventListener(
    'pointerleave',
    (event) => {
      const card = cardFrom(event);
      if (card) stop(card);
    },
    true
  );

  // ---- Touch: swipe + dots (Modeville cards) ----
  let touch = null;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const media = target.closest('.mv-card__media');
    if (!media || target.closest('.mv-dot')) return;
    const card = media.closest('.mv-card');
    if (!card || homeImages(card).length < 2) return;
    touch = { card, x: event.clientX, y: event.clientY, id: event.pointerId };
  });

  document.addEventListener('pointerup', (event) => {
    if (!touch || event.pointerId !== touch.id) return;
    const { card, x, y } = touch;
    touch = null;
    const dx = event.clientX - x;
    const dy = event.clientY - y;
    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    const count = homeImages(card).length;
    const from = current.get(card) || 0;
    preload(card);
    showHomeImage(card, (from + (dx < 0 ? 1 : -1) + count) % count);
    lastSwipe.set(card, Date.now());
  });

  document.addEventListener('pointercancel', () => {
    touch = null;
  });

  // A swipe must not also count as a tap on the product link.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const card = target.closest('.mv-card');
      if (!card) return;
      const dot = target.closest('.mv-dot');
      if (dot) {
        event.preventDefault();
        preload(card);
        showHomeImage(card, Number(dot.dataset.index) || 0);
        return;
      }
      if (target.closest('.mv-card__media') && Date.now() - (lastSwipe.get(card) || 0) < 400) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );
})();
